import {Organization} from "./Organization";
import {Umbrella} from "./Umbrella";
import {RateConfigDocument} from "./rate/RateConfig";

export class ServiceProvider {
  ID: number;
  Name: string;
  admin_ID: number;
  adminOrg_ID: number;
  /**
   * Whether this ServiceProvider is currently active. Backed by
   * `serviceprovider.active TINYINT(1) NOT NULL DEFAULT 1` on the
   * server side (added 2026-05-13). Inactive SPs are suppressed
   * from rate-config editor pickers and other "choose an SP"
   * surfaces; existing rate documents stored against inactive SPs
   * remain readable for historical / audit purposes.
   * See `docs/contracts/shared/ActiveFlags.md`.
   */
  active: boolean = true;
  collectionprovider_ID: number;
  signupHash: string | null;
  domain: string | null;
  overridePassword: string | null;
  defaultLabel_ID: number | null;
  invoiceServerURL: string | null;
  invoiceServerKey: string | null;
  invoiceClientId: number;
  invoiceRecurringId: number;
  firstName: string | null;
  lastName: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  email: string | null;
  phone: string | null;
  currency: string;
  crmServerURL: string | null;
  crmServerKey: string | null;
  productionRate: string | null; // Legacy product rates
  rateConfig: string | null; // Updated version of product rates they charge Organizations
  serviceProviderChargedRateConfig: string | null; // Updated version of rates Snowfall Press change service providers as the network
  /**
   * Packing & packaging rate configuration the SP charges its Organizations
   * for the picking, packing, and packaging step (parallel to {@link rateConfig}
   * but for packing rather than production). Same {@link RateConfigDocument}
   * shape — the engine reuses the existing factor model; the inputs at
   * evaluation time are package-aware (see shared/PackingRateConfig.md).
   */
  packingRateConfig: string | null;
  /**
   * Packing & packaging cost the network charges THIS SP per packing
   * operation (parallel to {@link serviceProviderChargedRateConfig}).
   */
  serviceProviderChargedPackingRateConfig: string | null;
  noticeEmail: string | null;
  tax: string | null;
  defaultOrganizationTax: string | null;
  lastUpdate: Date;
  defaultPackageLabelResource_ID: number;
  serviceProviderChargedProductionRate: string | null;
  smtpServer: string | null;
  smtpPort: string | null;
  emailUser: string | null;
  emailPassword: string | null;
  reportSalesTaxTransaction: string;
  organizations: Organization[];
  umbrellas: Umbrella[];
  catalogs: string;


  constructor ( obj ) {
    if ( obj ) {
      for ( const prop of Object.keys(obj) ) {
        if (prop === 'organizations') {
          for (const o of obj[prop]) {
            this.organizations.push(new Organization(o));
          }
        } else {
          if (prop === 'umbrellas') {
            for (const o of obj[prop]) {
              this.umbrellas.push(new Umbrella(o));
            }
          } else {
            this[prop] = obj[prop];
          }
        }
      }
      // Coerce `active` to a real boolean. MySQL TINYINT(1) often comes
      // across the wire as the numbers 0 / 1 rather than false / true,
      // which silently breaks the canonical `sp.active !== false`
      // filter (0 !== false is true). The contract
      // docs/contracts/shared/ActiveFlags.md treats undefined/missing as
      // active so legacy rows continue to render.
      if (obj.active === undefined || obj.active === null) {
        this.active = true;
      } else {
        this.active = !!obj.active && obj.active !== 0;
      }
    }
  }

  /**
   * Parse {@link rateConfig} (the rates this ServiceProvider charges its
   * Organizations) into a {@link RateConfigDocument}. Returns an empty
   * document when the column is null/invalid so the editor can populate it.
   *
   * Tolerates servers that hand back the column already-parsed (as a nested
   * object) rather than as a JSON string.
   */
  getRateConfig(): RateConfigDocument {
    const v: any = this.rateConfig;
    if (v && typeof v === 'object') { return v as RateConfigDocument; }
    if (typeof v === 'string' && v.length > 0) {
      try { return JSON.parse(v) as RateConfigDocument; }
      catch (e) { console.log(`Error parsing ServiceProvider.rateConfig for SP ${this.ID}`); }
    }
    return {};
  }

  /** Serialize a {@link RateConfigDocument} into the {@link rateConfig} column. */
  setRateConfig(doc: RateConfigDocument): void {
    this.rateConfig = JSON.stringify(doc || {});
  }

  /**
   * Parse {@link serviceProviderChargedRateConfig} (the rates the network
   * charges this ServiceProvider) into a {@link RateConfigDocument}.
   *
   * Tolerates already-parsed objects from the server (see {@link getRateConfig}).
   */
  getServiceProviderChargedRateConfig(): RateConfigDocument {
    const v: any = this.serviceProviderChargedRateConfig;
    if (v && typeof v === 'object') { return v as RateConfigDocument; }
    if (typeof v === 'string' && v.length > 0) {
      try { return JSON.parse(v) as RateConfigDocument; }
      catch (e) { console.log(`Error parsing ServiceProvider.serviceProviderChargedRateConfig for SP ${this.ID}`); }
    }
    return {};
  }

  /** Serialize a {@link RateConfigDocument} into the {@link serviceProviderChargedRateConfig} column. */
  setServiceProviderChargedRateConfig(doc: RateConfigDocument): void {
    this.serviceProviderChargedRateConfig = JSON.stringify(doc || {});
  }

  // ── Packing rate-config accessors ─────────────────────────────────
  // Parallel to the production rate-config accessors above. The on-disk
  // schema is identical (a `RateConfigDocument`); only the entity
  // column differs. Keeping the parsing/serialisation logic mirrored so
  // a future change to the rate-config wire format only has to be made
  // in one shape here.

  /**
   * Parse {@link packingRateConfig} (the packing/packaging rates this
   * ServiceProvider charges its Organizations) into a
   * {@link RateConfigDocument}. Returns `{}` on missing/invalid JSON.
   */
  getPackingRateConfig(): RateConfigDocument {
    const v: any = this.packingRateConfig;
    if (v && typeof v === 'object') { return v as RateConfigDocument; }
    if (typeof v === 'string' && v.length > 0) {
      try { return JSON.parse(v) as RateConfigDocument; }
      catch (e) { console.log(`Error parsing ServiceProvider.packingRateConfig for SP ${this.ID}`); }
    }
    return {};
  }

  /** Serialize a {@link RateConfigDocument} into the {@link packingRateConfig} column. */
  setPackingRateConfig(doc: RateConfigDocument): void {
    this.packingRateConfig = JSON.stringify(doc || {});
  }

  /**
   * Parse {@link serviceProviderChargedPackingRateConfig} (the packing
   * cost the network charges this ServiceProvider) into a
   * {@link RateConfigDocument}.
   */
  getServiceProviderChargedPackingRateConfig(): RateConfigDocument {
    const v: any = this.serviceProviderChargedPackingRateConfig;
    if (v && typeof v === 'object') { return v as RateConfigDocument; }
    if (typeof v === 'string' && v.length > 0) {
      try { return JSON.parse(v) as RateConfigDocument; }
      catch (e) { console.log(`Error parsing ServiceProvider.serviceProviderChargedPackingRateConfig for SP ${this.ID}`); }
    }
    return {};
  }

  /** Serialize a {@link RateConfigDocument} into the {@link serviceProviderChargedPackingRateConfig} column. */
  setServiceProviderChargedPackingRateConfig(doc: RateConfigDocument): void {
    this.serviceProviderChargedPackingRateConfig = JSON.stringify(doc || {});
  }
}

